import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useGetData from "../../api/useGetData";
import usePostData from "../../api/usePostData";
import { getArrivalTime } from "../../utils/getArrivalTime";

/**
 * A helper hook that returns the actions taken when one of the payment methods gets selected and submitted.
 * - If it is cash payment, it sends a request with the booking data and then navigates to the payment status page
 * - If it is payByLink payment, it sends the booking data to the backend and obtains the data of the generated link and QR code
 * - If it is credit card payment, it get a checkout from the backend, and then navigates the user to the credit card form page
 *
 * @param {Object} props
 * @param {Object} props.formValues - The values submitted in the form from the previous steps
 * @param {number} props.rideCost - The cost of the ride obtained from the backend
 * @param {string} props.proposal - The proposal for the payment obtained from the backend
 * @param {string} props.serviceType - The type of selected service
 */

const usePaymentMethods = ({
  formValues,
  selectedMethod,
  rideCost,
  proposal,
  serviceType,
}) => {
  window.history.replaceState({}, "");
  const { postData: postBooking } = usePostData();
  const { getData: getPaymentCheckout } = useGetData({
    route:
      "api/method/airport_transport.api.integrations.hyperpay.get_hyperpay_id",
    notLoadData: true,
  });
  const { postData: generateQRCode } = usePostData();
  const { user } = useSelector((state) => state.authData);
  const bookingSettings = useSelector(
    (state) => state.cachedRequests.bookingSettings
  );
  const bookingAllowedTime = (bookingSettings?.booking_allowed_time ?? 0) / 60;
  const navigate = useNavigate();
  
  const {
    t,
    i18n: { language },
  } = useTranslation("global");
  const bookingData = {
    airport: formValues.airportName,
    rider:
      formValues.sharedRide && formValues.seatNumber
        ? formValues.seatNumber
        : "",
    location: formValues.location,
    rideType: formValues.rideType,
    city: formValues.arrivalCity,
    terminal: formValues.terminalNumber,
    arrival_date: formValues.arrivalDate.format("YYYY-MM-DD"),
    arrival_time: getArrivalTime(
      formValues.arrivalTime,
      bookingAllowedTime,
      formValues.arrivalDate
    ),
    vehicle_type: formValues.vehicleType,
    ride_discount: formValues.discount,
    price: rideCost,
    ride_proposal: proposal,
    zone: formValues.zone,
    pick_up: `${formValues.locationCoords.lat},${formValues.locationCoords.lng}`,
    drop_off:
      formValues.destinationCoords &&
      `${formValues.destinationCoords.lat},${formValues.destinationCoords.lng}`,
    language,
    destination_name: formValues.destination,
    service_type: serviceType,
    payment_method: selectedMethod,
    booking_hours: formValues.hours || "",
    user: formValues.agentUser || undefined,
  };


  // _____________________________________________________________________________//
  const cashPayment = async () => {
    const response = await postBooking({
      route: "api/method/airport_transport.api.bookings.user_booking",
      data: bookingData,
      customError: true
    });
    if (response && response.status === 200) {
        message.success(t("booking_created"));
        navigate("/payment-status", { state: { paymentMethod: "Cash" }, });
    } else {
      message.error(response?.error);
      
    }
  };


  const creditCardPayment = async () => {

    const urlParams =  {
      amount: rideCost,
      card_type: selectedMethod === "Credit Card" ? "MasterCard" : selectedMethod,
      user: user?.agent ? formValues.agentUser : undefined,
    }
    if(selectedMethod === "Apple Pay") {
      urlParams.entityId = process.env.REACT_APP_APPLE_PAY_ENTRY_ID
    }
    const res = await getPaymentCheckout({
      params:urlParams,
      //  data:{entityId:process.env.REACT_APP_APPLE_PAY_ENTRY_ID}
    });
    const checkoutId = res.data.message.id;
    const integrity = res.data.message.integrity;
    navigate("/payment-confirmation", {
      state: {
        bookingData,
        checkoutId,
        integrity,
      },
    });
  };


  const payByLink = async () => {
    const queryObject = { ...bookingData };
    Object.keys(queryObject).forEach(
      (key) => queryObject[key] === undefined && delete queryObject[key]
    );
    const queryParams = new URLSearchParams(queryObject).toString();
    const successUrl =
      window.location.origin + `/payment-status?${queryParams}`;
    const payByLinkData = {
      "customer.email": user?.agent ? formValues.agentUser : user?.email,
      amount: rideCost,
      currency: "SAR",
      paymentType: "DB",
      shopperResultUrl: successUrl,
      createQRCode: true,
      doc: bookingData,
    };
    const response = await generateQRCode({
      route:
        "api/method/airport_transport.api.integrations.paybylink.generate_link",
      data: payByLinkData,
    });
    if (response.status === 200) {
      return {
        link: response.data.message.link,
        qrCode: response.data.message.qrCode,
      };
    } else {
      message.error("Couldn't generate link");
      return null;
    }
  };
  return { cashPayment, creditCardPayment, payByLink };
};

export default usePaymentMethods;
